import { isPlatformBrowser } from '@angular/common';
import { HttpClient, HttpErrorResponse } from '@angular/common/http';
import { inject, Injectable, isDevMode, PLATFORM_ID } from '@angular/core';
import { MiscUtils } from '../utils/misc.utils';
import { defer, Observable, of } from 'rxjs';
import { catchError, map, tap } from 'rxjs/operators';
import { NewRelicService } from './newrelic.service';
import { RequestDataService } from './request-data.service';

interface HermesResponse {
  topicName: string;
  correctness: 'ok' | 'unknown' | 'error';
  isRejected: boolean;
  errors?: string;
  unknowns?: string;
}

export interface GenericTrackBody {
  pageview_id: string;
  user_id: string;
  social_id?: string;
  event_datetime: string;
  is_logged: string;
  uow: string;
  type: 'system-action' | 'user-action';
  label: string;
  action: string;
  component: string;
  data: unknown;
}

@Injectable({
  providedIn: 'root',
})
export class UpaAsyncService {
  private requestDataService: RequestDataService = inject(RequestDataService);
  private httpClient: HttpClient = inject(HttpClient);
  private newRelicService: NewRelicService = inject(NewRelicService);
  private platformId = inject(PLATFORM_ID);

  trackToGeneric(
    type: 'system-action' | 'user-action',
    label: string,
    action: string,
    component: string,
    data?: Record<string, string | number | boolean | undefined>
  ): Observable<null> {
    const body: GenericTrackBody = {
      pageview_id: this.requestDataService.getPageviewId(),
      user_id: this.requestDataService.getTrackerId(),
      social_id: this.requestDataService.getSocialId(),
      event_datetime: new Date().toISOString(),
      is_logged: 'false', // TODO
      uow: this.requestDataService.getRequestData().uow,
      type,
      label,
      action,
      component,
      data,
    };
    return this.sendTrackAsBeacon('disrupt_generic', body);
  }

  // eslint-disable-next-line @typescript-eslint/no-explicit-any
  sendTrackAsBeacon(topicName: string, body: any): Observable<null> {
    const dataToTrack = this.buildDataToTrack(body.data);
    const bodyWithTransformedData = MiscUtils.removeUndefinedValues({
      ...body,
      data: dataToTrack,
    });
    if (this.canTrack()) {
      try {
        const blobProperties: BlobPropertyBag = {
          type: 'application/json',
        };
        const blob = new Blob(
          [JSON.stringify(bodyWithTransformedData)],
          blobProperties
        );
        navigator.sendBeacon(`/hermes-service/topic/${topicName}`, blob);
      } catch (error) {
        this.newRelicService.noticeError(
          new Error(
            `Failed to send beacon for topic ${topicName}: ${
              (error as Error).message
            }`
          )
        );
      }
    } else if (this.canLog()) {
      console.log(
        `Beacon tracking skipped for topic: ${topicName}, data`,
        bodyWithTransformedData
      );
    }

    return of(null);
  }

  // eslint-disable-next-line @typescript-eslint/no-explicit-any
  sendTrack(topicName: string, body: any): Observable<null> {
    const dataToTrack = this.buildDataToTrack(body.data);
    const bodyWithTransformedData = MiscUtils.removeUndefinedValues({
      ...body,
      data: dataToTrack,
    });
    if (this.canTrack()) {
      return this.httpClient
        .post<HermesResponse>(
          `/hermes-service/topic/${topicName}`,
          bodyWithTransformedData
        )
        .pipe(
          tap({
            next: (response: HermesResponse) => {
              if (body.label === 'pageview') {
                this.newRelicService.setCustomAttribute(
                  'hermes_status_code',
                  200
                );
                const relevant = this.extractRelevantHermesFields(response);
                const responseBody = this.truncateString(
                  JSON.stringify(relevant),
                  2000
                );
                this.newRelicService.setCustomAttribute(
                  'hermes_response_body',
                  responseBody
                );
              }
              this.handleHermesErrorResponse(response);
            },
            error: (response: HermesResponse | HttpErrorResponse) => {
              let statusCode: number;
              let responseBody: string | undefined;
              if (response instanceof HttpErrorResponse) {
                statusCode = response.status;
                responseBody = this.truncateString(
                  JSON.stringify(response.error),
                  2000
                );
                this.newRelicService.noticeError(
                  new Error(
                    `Topic tracking HTTP error for topic ${topicName}: ${response.message}`
                  )
                );
              } else {
                // Caso: HermesResponse u otro
                statusCode = -1;
                responseBody = this.truncateString(
                  JSON.stringify(response),
                  2000
                );
                this.handleHermesErrorResponse(response as HermesResponse);
              }
              this.newRelicService.setCustomAttribute(
                'hermes_status_code',
                statusCode
              );
              this.newRelicService.setCustomAttribute(
                'hermes_response_body',
                responseBody
              );
            },
          }),
          map(() => null),
          catchError((): Observable<null> => {
            return of(null);
          })
        );
    } else {
      return defer(() => {
        if (this.canLog()) {
          console.log(
            `Skipped tracking, topic: ${topicName}, data`,
            bodyWithTransformedData
          );
        }
        return of(null);
      });
    }
  }

  private buildDataToTrack(
    data?: Record<string, unknown>
  ): Record<string, string> {
    const initData: Record<string, string> = this.requestDataService.isTest()
      ? {
          is_test: '1',
        }
      : {};
    if (!data) return initData;
    return Object.keys(data).reduce((acc, currentValueKey) => {
      const value = data[currentValueKey];
      if (value === undefined) {
        return acc;
      }
      if (typeof value === 'string') {
        acc[currentValueKey] = value;
      }
      if (typeof value === 'boolean') {
        acc[currentValueKey] = value ? 'true' : 'false';
      }
      if (typeof value === 'number') {
        acc[currentValueKey] = String(value);
      }
      return acc;
    }, initData as Record<string, string>);
  }

  private canTrack(): boolean {
    return isPlatformBrowser(this.platformId) && !isDevMode();
  }

  private canLog(): boolean {
    return isPlatformBrowser(this.platformId) && isDevMode();
  }

  private handleHermesErrorResponse(response: HermesResponse): void {
    if (response.correctness !== 'ok' || response.errors || response.unknowns) {
      const errorFields = response.errors
        ? ` - Errors: ${response.errors}`
        : '';
      const unknownsFields = response.unknowns
        ? ` - Unknowns: ${response.unknowns}`
        : '';

      this.newRelicService.noticeError(
        new Error(
          `Topic tracking error ${response.topicName}${errorFields}${unknownsFields}`
        )
      );
    }
  }

  /**
   * Trunca un string si supera el tamaño máximo especificado.
   */
  private truncateString(str: string, maxLength: number): string {
    if (str.length > maxLength) {
      return str.substring(0, maxLength) + '... [truncated]';
    }
    return str;
  }

  /**
   * Extrae los campos relevantes de HermesResponse para logging.
   */
  private extractRelevantHermesFields(
    response: HermesResponse
  ): Partial<HermesResponse> {
    return {
      topicName: response.topicName,
      correctness: response.correctness,
      isRejected: response.isRejected,
      errors: response.errors,
      unknowns: response.unknowns,
    };
  }
}
